<template>
    <div class="oak-table-root">
        <v-card class="oak-table-card">
            <div class="oak-table-toolbar">
                <v-spacer/>
                <v-btn
                    icon
                    small
                    :title="expanded ? $t('journey.oakTable.collapseTitle') : $t('journey.oakTable.expandTitle')"
                    @click="expanded = !expanded"
                >
                    <v-icon size="22" :color="expanded ? 'primary' : undefined">
                        {{ expanded ? 'mdi-arrow-collapse-horizontal' : 'mdi-arrow-expand-horizontal' }}
                    </v-icon>
                </v-btn>
            </div>
            <v-data-table
                :loading="loading"
                :headers="visibleHeaders"
                :items="allPokemons"
                :options.sync="tableOptions"
                :server-items-length="totalItems"
                item-key="id"
                dense
                fixed-header
                height="220"
                :footer-props="{ itemsPerPageOptions: [10, 20, 30, 50] }"
                :header-props="{ sortIcon: null }"
                :mobile-breakpoint="0"
                class="oak-table"
                @click:row="(item) => showDetailPokemon(item.id)"
            >
                <template v-slot:[`item.avatar`]="{ item }">
                    <v-avatar size="28" class="oak-table-avatar">
                        <v-img :src="pokemonIcon(item)" cover/>
                        <!-- hvězdička = "může se naučit nový skill", stejná konvence jako PokemonManager.vue/TrainerDetail.vue -->
                        <v-icon
                            v-if="item.posibleBuySkill"
                            size="14"
                            class="oak-table-avatar-star"
                        >
                            mdi-star
                        </v-icon>
                    </v-avatar>
                </template>
                <template v-slot:[`item.name`]="{ item }">
                    {{ item.name }}
                    <!-- hvězdička = evolvable pokémon odložený u Oaka (viz roadmap.md) -->
                    <v-icon
                        v-if="item.evolvable && item.evolvable.length > 0"
                        size="14"
                        class="oak-table-name-star"
                    >
                        mdi-star
                    </v-icon>
                </template>
                <template v-slot:[`item.hp`]="{ item }">
                    {{ item.hp }}/{{ item.maxHp }}
                </template>
                <template v-slot:[`item.inProfesorOak`]="{ item }">
                    <v-btn
                        rounded
                        :loading="loading"
                        x-small
                        :class="item.inProfesorOak ? 'skin-btn-box' : 'skin-btn-team'"
                        :disabled="trainerPokemonsCount >= pokemonPool && item.inProfesorOak"
                        @click.stop="switchProfOakAndReload(item.id, item.inProfesorOak)"
                    >
                        {{ item.inProfesorOak ? $t('journey.oakTable.oakLabel') : $t('common.you') }}
                    </v-btn>
                </template>
            </v-data-table>
        </v-card>
        <v-card v-if="showDetail && pokemonDetail" class="oak-table-detail-card">
            <PokemonManager
                :in-oak="true"
                :pokemon-id="pokemonDetail.id"
                :delete-pokemon-callback="deletePokemonCallback"
                :isFullPool="trainerPokemonsCount >= pokemonPool"
                :update-pokemon="onPokemonUpdated"
                :switch-prof-oak="switchProfOak"
                :buy-skill="buySkill"
                :height-image="300"
            />
        </v-card>
    </div>
</template>

<script>
    // Sdílená tabulka + detail správy pokémonů u Oaka (OakNext.vue i
    // OakDialog.vue) - viz claude/components/journey-oak-league.md.
    //
    // Načítání jde přes `pokemon/fetchObject` (GET, syrová data), ne
    // `pokemon/loadItems` - ten by přepsal sdílený `pokemon/items` seznam,
    // do kterého zapisuje i TeamBottomSheet.vue s užším filtrem (aktivní tým
    // max 6). `allPokemons` je proto čistě lokální kopie.
    import {mapActions} from 'vuex';
    import PokemonManager from '@/components/pokemon/PokemonManager';
    import * as pokemonApi from '@/api/pokemonApi';
    import {pokemonImageSrc} from '@/utils/pokemonImage';

    export default {
        name: 'OakTable',
        components: {PokemonManager},
        props: {
            trainerId: {type: Number, required: true},
        },
        data() {
            return {
                pokemonPool: 6,
                trainerPokemonsCount: 0,
                showDetail: false,
                pokemonDetail: null,
                loading: false,
                // jen aktuální stránka - server-side stránkování (viz roadmap.md)
                allPokemons: [],
                totalItems: 0,
                tableOptions: {page: 1, itemsPerPage: 10, sortBy: [], sortDesc: []},
                expanded: false,
            }
        },
        computed: {
            // musí být computed, ne data() - obsahuje $t() (viz i18n konvence bod 8)
            coreHeaders() {
                return [
                    {text: '', value: 'avatar', sortable: false, width: 40},
                    {text: this.$t('journey.oakTable.nameHeader'), value: 'name', sortable: true},
                    {text: this.$t('journey.oakTable.totalExpHeader'), value: 'pokemonExperience.exp', sortable: true, align: 'end'},
                    {text: this.$t('journey.oakTable.freeExpHeader'), value: 'pokemonExperience.freeExp', sortable: true, align: 'end'},
                    {text: this.$t('journey.oakTable.lvlHeader'), value: 'level', sortable: true, align: 'end'},
                    {text: this.$t('journey.oakTable.whereHeader'), value: 'inProfesorOak', sortable: true, align: 'end'},
                ];
            },
            expandableHeaders() {
                return [
                    {text: this.$t('journey.oakTable.hpHeader'), value: 'hp', sortable: true, align: 'end'},
                ];
            },
            visibleHeaders() {
                const withoutWhere = this.coreHeaders.slice(0, -1);
                const whereHeader = this.coreHeaders[this.coreHeaders.length - 1];
                if (!this.expanded) {
                    return this.coreHeaders;
                }
                return [...withoutWhere, ...this.expandableHeaders, whereHeader];
            },
        },
        watch: {
            trainerId: {
                immediate: true,
                handler() {
                    if (this.trainerId) {
                        this.reloadList();
                        this.reloadTeamCount();
                    }
                },
            },
            // server-side stránkování - každá změna stránky/řazení musí znovu dotáhnout data
            tableOptions: {
                deep: true,
                handler() {
                    this.reloadList();
                },
            },
            // po rozbalení doscrollovat úplně doprava, ať je "Where" vidět -
            // "position: sticky" na sloupci zkoušená dřív překrývala HP hodnotu pod sebou
            expanded() {
                if (this.expanded) {
                    this.$nextTick(() => {
                        const wrapper = this.$el.querySelector('.oak-table .v-data-table__wrapper');
                        if (wrapper) {
                            wrapper.scrollLeft = wrapper.scrollWidth;
                        }
                    });
                }
            },
        },
        methods: {
            ...mapActions({
                updatePokemon: 'pokemon/updateItem',
                fetchPokemon: 'pokemon/fetchObject',
            }),
            pokemonIcon(p) {
                return pokemonImageSrc(p.pokemonType.img);
            },
            async switchProfOak(id, oak) {
                if (oak) {
                    this.trainerPokemonsCount++;
                } else {
                    this.trainerPokemonsCount--;
                }
                // počkat na dokončení PATCH, jinak TeamBottomSheet.vue refresh často předběhne uložení na serveru
                await this.updatePokemon({id, inProfesorOak: !oak, edited: new Date()});
                const temp = this.allPokemons.find((item) => item.id === id);
                temp.inProfesorOak = !oak;
                this.$emit('team-changed');
            },
            switchProfOakAndReload(id, oak) {
                if (this.pokemonDetail !== null && id === this.pokemonDetail.id) {
                    this.showDetail = false;
                }
                this.switchProfOak(id, oak);
            },
            buySkill(pokemon) {
                const found = this.allPokemons.find((item) => item.id === pokemon.id);
                if (found) {
                    found.pokemonExperience.freeExp = pokemon.pokemonExperience.freeExp;
                    found.posibleBuySkill = pokemon.posibleBuySkill;
                }
            },
            reloadList() {
                if (!this.trainerId) {
                    return;
                }
                this.loading = true;
                const {page, itemsPerPage, sortBy, sortDesc} = this.tableOptions;
                this.fetchPokemon(pokemonApi.pokemonListPaged(this.trainerId, {
                    page,
                    itemsPerPage,
                    sortBy: sortBy && sortBy[0] ? sortBy[0] : null,
                    sortDesc: !!(sortDesc && sortDesc[0]),
                })).then((data) => {
                    this.allPokemons = (data && data.member) || [];
                    this.totalItems = (data && data.totalItems) || 0;
                    this.loading = false;
                });
            },
            // team-changed - reloadList() refreshuje jen lokální tabulku Oaka, ne sdílený store/TeamBottomSheet.vue
            onPokemonUpdated() {
                this.reloadList();
                this.$emit('team-changed');
            },
            // po stránkování `allPokemons` neobsahuje všechny řádky - vlastní lehký dotaz na počet aktivního týmu
            reloadTeamCount() {
                this.fetchPokemon(pokemonApi.pokemonActiveTeamCount(this.trainerId)).then((data) => {
                    this.trainerPokemonsCount = (data && data.totalItems) || 0;
                });
            },
            showDetailPokemon(id) {
                this.pokemonDetail = this.allPokemons.find((p) => p.id === id);
                this.showDetail = !!this.pokemonDetail;
            },
            deletePokemonCallback(id) {
                if (this.pokemonDetail && id === this.pokemonDetail.id) {
                    this.showDetail = false;
                }
                // po stránkování nejde jen odebrat řádek lokálně - přenačíst aktuální stránku + počet týmu
                this.reloadList();
                this.reloadTeamCount();
                this.$emit('team-changed');
            },
        },
    }
</script>

<style scoped>
.oak-table-root {
    display: flex;
    flex-direction: column;
    gap: 12px;
}
.oak-table-card {
    flex: none;
    padding: 0 2px 4px 2px;
}
.oak-table-toolbar {
    display: flex;
    align-items: center;
    padding: 2px 4px;
}
.oak-table-avatar {
    position: relative;
}
.oak-table-avatar-star {
    position: absolute;
    top: -4px;
    right: -4px;
    color: #FFD700 !important;
    filter: drop-shadow(0 0 2px rgba(0,0,0,0.9)) drop-shadow(0 0 2px rgba(0,0,0,0.9));
}
.oak-table-name-star {
    color: #FFD700 !important;
    position: relative;
    top: -1px;
}
/* menší rozestupy mezi sloupci než Vuetify default (16px) - kompaktní tabulka na 360px šířku;
   ::v-deep nutné, v-data-table renderuje vlastní markup mimo dosah scoped stylu */
.oak-table ::v-deep th,
.oak-table ::v-deep td {
    padding: 0 6px !important;
    font-size: 12px !important;
}
.oak-table ::v-deep th:first-child,
.oak-table ::v-deep td:first-child {
    padding-left: 10px !important;
}
.oak-table-detail-card {
    flex: none;
}
</style>
